/**
 * Cut a Portal clip down to its TRIM window on the device, before a byte leaves it.
 *
 * Packets are copied, never re-encoded (Mediabunny, `copy.mode: 'forced'`), so a 124 MB phone
 * video becomes its 30-second slice in well under a second and at the quality it was shot.
 * The cut starts at the key frame before the window (`boundaryPolicy: 'expand'`) and the MP4's
 * edit list hides that lead-in, so the new file's timeline starts exactly where the sender's
 * window did: time 0 in the cut is `startMs` in the original.
 *
 * Returns `{ file, startMs, clipMs }`, or `null` when there's nothing to cut or it can't be cut
 * by copying (an odd codec, a track that won't copy, a browser without the APIs). The caller then
 * uploads the original, which is what happened before this existed.
 *
 * Mediabunny is loaded on demand, so it's only downloaded by someone actually sending a clip.
 */
const CUT_TIMEOUT_MS = 20000;

export default async function trimVideo(file, { startMs, endMs }) {
  if (!file || !(endMs > startMs)) return null;
  try {
    return await Promise.race([
      cut(file, startMs, endMs),
      new Promise((resolve) => { setTimeout(() => resolve(null), CUT_TIMEOUT_MS); }),
    ]);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Portal clip could not be cut on the device; sending the whole file.', e);
    return null;
  }
}

async function cut(file, startMs, endMs) {
  const mb = await import('mediabunny');
  const input = new mb.Input({ source: new mb.BlobSource(file), formats: mb.ALL_FORMATS });
  const durationSec = await input.computeDuration();
  // The window already is the whole file: nothing to take off.
  if (startMs <= 50 && endMs >= durationSec * 1000 - 50) return null;

  const output = new mb.Output({
    format: new mb.Mp4OutputFormat({ fastStart: 'in-memory' }),
    target: new mb.BufferTarget(),
  });
  const conversion = await mb.Conversion.init({
    input,
    output,
    trim: { start: startMs / 1000, end: endMs / 1000 },
    copy: { mode: 'forced', boundaryPolicy: 'expand', boundaryTolerance: Infinity, shiftTolerance: Infinity },
    showWarnings: false,
  });
  // A clip that lost its picture isn't a clip.
  if (!conversion.isValid || conversion.discardedTracks.some((d) => d.track?.type === 'video')) return null;
  await conversion.execute();

  const buffer = output.target.buffer;
  if (!buffer || buffer.byteLength === 0) return null;
  const name = (file.name || 'portal').replace(/\.[^.]+$/, '') + '.mp4';
  return { file: new File([buffer], name, { type: 'video/mp4' }), startMs, clipMs: endMs - startMs };
}
